import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Modal,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import PeopleIcon from '@mui/icons-material/People'
import DescriptionIcon from '@mui/icons-material/Description'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { useTranslation } from 'react-i18next'
import CustomButton from './CustomButton'
import useVideoPath from '../hooks/useVideoPath'

const VIDEO_OPTIONS = [
  {
    id: 'search',
    titleKey: 'legal_search_engine',
    descriptionKey: 'search_tutorial_description',
    filename: 'search.mp4',
    Icon: SearchIcon,
  },
  {
    id: 'chat',
    titleKey: 'conversation',
    descriptionKey: 'chat_tutorial_description',
    filename: 'chat.mp4',
    Icon: PeopleIcon,
  },
  {
    id: 'contract',
    titleKey: 'contract_dashboard',
    descriptionKey: 'contract_tutorial_description',
    filename: 'clm.mp4',
    Icon: DescriptionIcon,
  },
  {
    id: 'news',
    titleKey: 'legal_news_hub',
    descriptionKey: 'news_tutorial_description',
    filename: 'watch.mp4',
    Icon: TrendingUpIcon,
  },
]

const TutorialModal = ({ open, onClose, defaultTab = 'search' }) => {
  const { t } = useTranslation()
  const getVideoUrl = useVideoPath()
  const theme = useTheme()
  const [selectedVideo, setSelectedVideo] = useState(defaultTab)

  useEffect(() => {
    if (open) setSelectedVideo(defaultTab)
  }, [open, defaultTab])

  const videoOptions = VIDEO_OPTIONS.map((v) => ({
    ...v,
    title: t(v.titleKey),
    description: t(v.descriptionKey),
    url: getVideoUrl(v.filename),
    icon: <v.Icon sx={{ fontSize: 18 }} />,
  }))

  const currentVideo = videoOptions.find((v) => v.id === selectedVideo)

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 980,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Modal header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayCircleIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {t('tutorial_video')}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.2) },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Modal body */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* Sidebar */}
          <Box
            sx={{
              width: 260,
              flexShrink: 0,
              borderRight: `1px solid ${theme.palette.divider}`,
              p: 1.5,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {videoOptions.map((video) => {
              const active = selectedVideo === video.id
              return (
                <Box
                  key={video.id}
                  onClick={() => setSelectedVideo(video.id)}
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    border: `1px solid ${
                      active
                        ? alpha(theme.palette.primary.main, 0.5)
                        : 'transparent'
                    }`,
                    bgcolor: active
                      ? alpha(theme.palette.primary.main, 0.06)
                      : alpha(theme.palette.background.default, 0.5),
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box
                      sx={{
                        color: active
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      }}
                    >
                      {video.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: active ? 600 : 500,
                        color: active
                          ? theme.palette.text.primary
                          : theme.palette.text.secondary,
                        fontSize: '0.85rem',
                      }}
                    >
                      {video.title}
                    </Typography>
                  </Box>
                  {video.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.disabled,
                        fontSize: '0.72rem',
                        display: 'block',
                        ml: 3.5,
                        lineHeight: 1.4,
                      }}
                    >
                      {video.description}
                    </Typography>
                  )}
                </Box>
              )
            })}
          </Box>

          {/* Video player */}
          <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box
              sx={{
                position: 'relative',
                paddingTop: '56.25%',
                flex: 1,
                borderRadius: 1.5,
                overflow: 'hidden',
                background: '#000',
              }}
            >
              <video
                key={selectedVideo}
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                }}
                controls
                autoPlay
                muted
              >
                <source src={currentVideo?.url} type="video/mp4" />
              </video>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 1.75,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <CustomButton
            text={t('close')}
            variant="contained"
            onClick={onClose}
            sx={{ minWidth: 100, borderRadius: 1 }}
          />
        </Box>
      </Box>
    </Modal>
  )
}

export default TutorialModal
